import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ScoreGauge from "@/components/ScoreGauge";
import { motion } from "framer-motion";
import {
  Target, Sparkles, ScanLine, ArrowRight, CheckCircle2, AlertCircle,
  Users, Gauge, HelpCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { PRIORITY_LABELS } from "@/lib/i18n";
import { limitsFor } from "@/lib/plan";
import UpgradeNudge from "@/components/UpgradeNudge";
import ScoreInsightCard from "@/components/ScoreInsightCard";
import RescanProgressOverlay from "@/components/RescanProgressOverlay";

const SUB_KEYS = {
  anlasilabilirlik: "score.sub.anlasilabilirlik.label",
  bulunabilirlik: "score.sub.bulunabilirlik.label",
  onerilebilirlik: "score.sub.onerilebilirlik.label",
  icerik_gucu: "score.sub.icerik_gucu.label",
  guven_sinyali: "score.sub.guven_sinyali.label",
  lokasyon_netligi: "score.sub.lokasyon_netligi.label",
  hizmet_netligi: "score.sub.hizmet_netligi.label",
  niyet_kapsami: "score.sub.niyet_kapsami.label",
};

export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const canRescan = (limits.monthly_rescan || 0) > 0;
  const [biz, setBiz] = useState(null);
  const [scan, setScan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      api.get("/businesses").then((r) => {
        if (cancelled) return;
        // Bail out if the user has already navigated away (e.g. clicked a sidebar link).
        // window.location.pathname is read at resolve-time to catch the latest URL.
        if (window.location.pathname !== "/app") return;
        if (r.data.length === 0) navigate("/app/onboarding", { replace: true });
        else navigate(`/app/isletme/${r.data[0].id}`, { replace: true });
      });
      return () => { cancelled = true; };
    }
    setLoading(true);
    Promise.all([
      api.get(`/businesses/${id}`).then((r) => r.data),
      api.get(`/scans/${id}/latest`, { params: { lang } }).then((r) => r.data).catch(() => null),
      api.get(`/tasks/${id}`, { params: { lang } }).then((r) => r.data).catch(() => []),
    ]).then(([b, s, t]) => {
      if (cancelled) return;
      setBiz(b); setScan(s); setTasks(t); setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, navigate, location.pathname, lang]);

  if (loading || !id) {
    return <div className="flex items-center justify-center min-h-[60vh] text-slate-400"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;
  }

  if (!biz) {
    return (
      <div className="glass-strong rounded-3xl p-10 text-center max-w-2xl mx-auto" data-testid="empty-business-state">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-white/10 items-center justify-center mb-4">
          <Target size={26} className="text-cyan-300" strokeWidth={1.5} />
        </div>
        <h2 className="font-display text-2xl font-semibold text-white mb-2">{t("dash.empty.title")}</h2>
        <p className="text-slate-300 mb-6">{t("dash.empty.desc")}</p>
        <Link to="/app/onboarding" className="neon-btn rounded-xl px-7 py-3 text-white font-semibold inline-flex items-center gap-2">
          {t("dash.empty.cta")} <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  // Backend translates priority/status to English when lang=en. Match both
  // canonical Turkish and translated English so dashboard counts stay
  // correct across language switches.
  const isCompleted = (s) => s === "Tamamlandı" || s === "Completed";
  const isCritical = (p) => p === "Kritik" || p === "Critical";
  const completedTasks = tasks.filter((task) => isCompleted(task.status)).length;
  const criticalTasks = tasks.filter((task) => isCritical(task.priority) && !isCompleted(task.status));
  const remainingTasks = tasks.length - completedTasks;
  const isFreePlan = (user?.plan || "free") === "free";

  const handleRescan = async () => {
    setRescanning(true);
    try {
      // /rescan returns immediately with a run_id when it triggers a full
      // re-analysis (matching the new /analyze polling pattern). Per-task
      // rescans short-circuit on the backend and don't reach this branch.
      const { data: startRes } = await api.post("/rescan", { business_id: id });
      const runId = startRes?.run_id;
      if (runId) {
        const maxAttempts = 105;
        let attempt = 0;
        let finalStatus = "running";
        let lastError = "";
        while (attempt < maxAttempts) {
          attempt += 1;
          await new Promise((r) => setTimeout(r, 2000));
          const statusRes = await api.get(`/analysis-runs/${runId}`);
          finalStatus = statusRes?.data?.status;
          if (finalStatus === "done") break;
          if (finalStatus === "error") {
            lastError = statusRes?.data?.error || "";
            break;
          }
        }
        if (finalStatus !== "done") throw new Error(lastError || t("dash.rescanError"));
      }
      // Refresh dashboard data after the run completes
      const newScan = await api.get(`/scans/${id}/latest`, { params: { lang } }).then((r) => r.data);
      setScan(newScan);
      const newTasks = await api.get(`/tasks/${id}`, { params: { lang } }).then((r) => r.data);
      setTasks(newTasks);
      const b = await api.get(`/businesses/${id}`).then((r) => r.data);
      setBiz(b);
      toast.success(t("dash.rescanSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dash.rescanFatal"));
    } finally {
      setRescanning(false);
    }
  };

  if (!scan) {
    return (
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{biz.sector} · {biz.city}</div>
          <h1 className="font-display text-4xl font-bold text-white">{biz.name}</h1>
        </div>
        <div className="glass-strong rounded-3xl p-10 text-center">
          <ScanLine size={48} className="mx-auto text-cyan-400 mb-4" strokeWidth={1.4} />
          <h2 className="font-display text-2xl font-semibold text-white mb-2">{t("dash.noScan.title")}</h2>
          <p className="text-slate-300 mb-6">{t("dash.noScan.desc")}</p>
          <Link
            to={`/app/isletme/${id}/analiz`}
            className="neon-btn rounded-xl px-7 py-3 text-white font-semibold inline-flex items-center gap-2"
            data-testid="start-analysis-cta"
          >
            {t("dash.noScan.cta")} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const prevScore = biz.previous_score;
  const delta = prevScore != null ? scan.overall_score - prevScore : 0;
  const competitorCount = scan.competitor_signals?.filter((c) => c.ok).length || 0;

  return (
    <div className="space-y-6">
      <RescanProgressOverlay open={rescanning} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{t("dash.overviewEyebrow", { sector: biz.sector, city: biz.city })}</div>
          <h1 className="font-display text-4xl font-bold text-white" data-testid="biz-name">{biz.name}</h1>
          {biz.website_url && <a href={biz.website_url} target="_blank" rel="noreferrer" className="text-sm text-slate-300 hover:text-cyan-300">{biz.website_url}</a>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canRescan ? (
            <button
              onClick={handleRescan}
              disabled={rescanning}
              className="ghost-btn rounded-xl px-4 py-2.5 text-sm text-white inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="dashboard-rescan"
            >
              {rescanning ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
              {t("dash.rescan")}
            </button>
          ) : (
            <Link
              to="/app/planlar"
              className="ghost-btn rounded-xl px-4 py-2.5 text-sm text-cyan-200 border border-cyan-500/30 inline-flex items-center gap-2"
              data-testid="dashboard-rescan-locked"
              title={t("dash.rescan.tooltip")}
            >
              <ScanLine size={14} /> {t("dash.rescan.locked")}
            </Link>
          )}
          <Link to={`/app/isletme/${id}/icerik`} className="ghost-btn rounded-xl px-4 py-2.5 text-sm text-white inline-flex items-center gap-2" data-testid="dashboard-content">
            <Sparkles size={14} /> {t("dash.createContent")}
          </Link>
          <Link to={`/app/isletme/${id}/gorevler`} className="neon-btn rounded-xl px-4 py-2.5 text-sm text-white inline-flex items-center gap-2 font-medium" data-testid="dashboard-tasks">
            <Target size={14} /> {t("dash.goToTasks")}
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-1 glass-strong rounded-3xl p-7 card-glow">
          <div className="text-xs uppercase tracking-wider text-slate-300 mb-2">{t("dash.scoreLabel")}</div>
          <ScoreGauge score={scan.overall_score} label={t("dash.overallScoreLabel")} />
          {prevScore != null && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm">
              <span className="text-slate-400">{t("dash.prev")} <span className="font-mono text-slate-200">{prevScore}</span></span>
              <span className={`font-mono ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>{delta > 0 ? `+${delta}` : delta}</span>
            </div>
          )}
          <div className="mt-4 rounded-lg bg-white/[0.02] border border-white/5 p-3 text-xs text-slate-300 leading-relaxed">
            {t("dash.scoreCaveat")}
          </div>
          <Link to={`/app/isletme/${id}/skor`} className="block text-center mt-3 text-sm text-cyan-300 hover:text-cyan-200 font-medium" data-testid="dashboard-score-detail">
            {t("dash.detailedScore")}
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-2 glass rounded-3xl p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-wider text-slate-300">{t("dash.subscores")}</div>
            <Link to={`/app/isletme/${id}/skor`} className="text-xs text-cyan-300 hover:text-cyan-200 font-medium">{t("common.viewAll")}</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(scan.subscores || {}).map(([k, v]) => (
              <SubscoreBar key={k} label={SUB_KEYS[k] ? t(SUB_KEYS[k]) : k} value={v} />
            ))}
          </div>
          <p className="text-sm text-slate-300 mt-5 leading-relaxed border-t border-white/5 pt-4">{scan.score_explanation}</p>
        </motion.div>
      </div>

      <ScoreInsightCard insight={scan.score_change_insight} businessId={id} />

      <div className="grid md:grid-cols-4 gap-4">
        <NavTile to={`/app/isletme/${id}/skor`} icon={Gauge} label={t("dash.tile.aiScore")} value={scan.overall_score} accent="text-cyan-300" />
        <NavTile to={`/app/isletme/${id}/sorular`} icon={HelpCircle} label={t("dash.tile.questions")} value={scan.ai_question_tests?.length || 0} accent="text-blue-300" sub={t("dash.tile.questionsSub")} />
        <NavTile to={`/app/isletme/${id}/rakipler`} icon={Users} label={t("dash.tile.competitors")} value={competitorCount} accent="text-purple-300" sub={t("dash.tile.competitorsSub")} />
        <NavTile to={`/app/isletme/${id}/gorevler`} icon={Target} label={t("dash.tile.tasks")} value={`${completedTasks}/${tasks.length}`} accent="text-emerald-300" sub={t("dash.tile.tasksSub")} />
      </div>

      <div className="glass-strong rounded-3xl p-7">
        <div className="flex items-center justify-between mb-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-purple-300 mb-1">{t("dash.today")}</div>
            <h2 className="font-display text-2xl font-semibold text-white">{t("dash.criticalToday")}</h2>
          </div>
          <Link to={`/app/isletme/${id}/gorevler`} className="text-sm text-cyan-300 hover:text-cyan-200 font-medium shrink-0" data-testid="see-all-tasks">
            {t("common.viewAll")}
          </Link>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed mb-4 max-w-2xl" data-testid="dash-critical-intro">
          {t("dash.criticalIntro")}
        </p>
        <div className="space-y-2">
          {criticalTasks.slice(0, 3).length === 0 && (
            <div className="text-slate-400 text-sm">{t("dash.noCriticalLeft")}</div>
          )}
          {criticalTasks.slice(0, 3).map((task) => (
            <Link
              key={task.id}
              to={`/app/isletme/${id}/gorevler`}
              className="block rounded-xl p-4 bg-white/[0.02] border border-white/5 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all"
              data-testid={`dash-task-${task.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-white truncate">{task.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{task.category} · {t("dash.impact", { n: task.impact_points })}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 shrink-0">
                  {PRIORITY_LABELS[lang]?.[task.priority] || task.priority}
                </span>
              </div>
            </Link>
          ))}
        </div>
        {remainingTasks > 0 ? (
          <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5" data-testid="dash-tasks-remaining">
            <Sparkles size={11} className="text-cyan-300" />
            {t("dash.tasksRemaining", { n: remainingTasks })}
          </p>
        ) : tasks.length > 0 ? (
          <p className="text-xs text-emerald-300 mt-4 flex items-center gap-1.5" data-testid="dash-tasks-all-done">
            <CheckCircle2 size={11} />
            {t("dash.tasksAllDone")}
          </p>
        ) : null}
      </div>

      {isFreePlan && <UpgradeNudge testid="dashboard-upgrade-nudge" />}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="glass-strong rounded-3xl p-6 border border-red-500/15">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={16} className="text-red-400" />
            <h3 className="font-display font-semibold text-white">{t("dash.criticalGapsTitle")}</h3>
          </div>
          <ul className="space-y-2 text-sm text-slate-300">
            {(scan.weaknesses || []).slice(0, 3).map((s, i) => (
              <li key={i} className="flex gap-2.5 items-start rounded-lg bg-red-500/5 border border-red-500/10 p-3">
                <span className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-[10px] font-mono text-red-300 shrink-0">{i + 1}</span>
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
            {(scan.weaknesses || []).length === 0 && (
              <li className="text-slate-400">{t("dash.noWeaknesses")}</li>
            )}
          </ul>
        </div>
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={16} className="text-emerald-400" /><h3 className="font-display font-semibold text-white">{t("dash.strengthsTitle")}</h3></div>
          <ul className="space-y-2 text-sm text-slate-300">
            {(scan.strengths || []).map((s, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" />{s}</li>
            ))}
            {(scan.strengths || []).length === 0 && <li className="text-slate-400">{t("dash.noStrengths")}</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SubscoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 70 ? "from-emerald-400 to-cyan-400" : v >= 40 ? "from-amber-400 to-orange-500" : "from-red-500 to-pink-500";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5 text-sm">
        <span className="text-slate-200">{label}</span>
        <span className="font-mono text-white">{v}</span>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color} rounded-full`} style={{ width: `${v}%`, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

function NavTile({ to, icon: Icon, label, value, accent, sub }) {
  return (
    <Link to={to} className="glass rounded-2xl p-5 card-glow group">
      <div className="flex items-center justify-between mb-3">
        <Icon size={18} className={accent} strokeWidth={1.6} />
        <ArrowRight size={14} className="text-slate-500 group-hover:text-slate-200 transition-colors" />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-slate-300 mb-1">{label}</div>
      <div className="font-mono text-2xl text-white">{value}</div>
      {sub && <div className="text-xs text-slate-300 mt-0.5">{sub}</div>}
    </Link>
  );
}
